import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AuthenticationService } from 'src/app/_services';
import { ApiService } from 'src/app/api.service';
import { SecurityDocument } from 'src/app/shared/models/SecurityDocument';
import { TovpilCrudService } from 'src/app/utils/tovpil-crud-service';
import { TovpilLanguageService } from 'src/app/utils/tovpil-language.service';
import { first } from 'rxjs/operators';
import { Constants } from 'src/app/shared/vo/Constants';
import { ResponseFile } from 'src/app/shared/models/ResponseFile';
import { CookieTovpil } from 'src/app/shared/models/CookieTovpil';
import { UserPreferencesService } from 'src/app/utils/user-preferences.service';
import { UrlBuilderService } from 'src/app/utils/tovpil-url-builder.service';

@Component({
  selector: 'app-fi-summary',
  templateUrl: './fi-summary.component.html',
  styleUrls: ['./fi-summary.component.scss'],
  providers: [MessageService, ConfirmationService, TovpilLanguageService]
})
export class FiSummaryComponent implements OnInit {

  //User Preferences
  globalUserPreferences: CookieTovpil = {};
  globalUserDataForm: FormGroup;

  // Security
  currentUser: any;
  security: SecurityDocument;
  token: string;
  device: string;
  storeId: string;
  datePipe: DatePipe = new DatePipe('en-US');

  // LABELS
  translations: Record<string, string> = {};
  hoursLabels;
  hourLabelsTranslated = {
    plw_adults: "",
    plw_yadults: "",
    plw_adol: "",
    plw_child: "",
    plw_mar_cour: "",
    plw_sfpg: "",
    mar_cour: ""
  };

  /** COMPONENT OVERLAY */
  blocked: boolean = true;
  coordName: string;

  /** LANGUAJE FORM */
  languagesLst: { label: string; value: string; }[];

  // ENDPOINTS
  endPointLanguageByStoreId = "getListSpkLangByStoreId";


  data: Record<string, any>[];

  periods: any[] = [
    { name: '1st Period', key: '1' },
    { name: '2nd Period', key: '2' },
    { name: 'Yearly', key: '0' },
  ];

  listYears: any[];


  listCurrencies: any[];
  responseFile?: ResponseFile;

  constructor(
    private messageService: MessageService,
    private apiService: ApiService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    private tovpilCrudService: TovpilCrudService,
    private fb: FormBuilder,
    private userPreferencesService: UserPreferencesService,
    private translationService: TovpilLanguageService,
    private urlBuilderService: UrlBuilderService) {

    this.createDataForm();
    this.initConfigs();
  }

  ngOnInit(): void {
    this.currentUser = this.authenticationService.currentUserValue;
    this.getSecurity();
  }


  /**
   * The function `autenthicateByZuul` authenticates a user by calling the `login` method of the
   * `authenticationService` and performs additional actions based on the response.
   * @param {string} user - The `user` parameter is a string that represents the username or user
   * identifier for authentication.
   * @param {string} password - The password parameter is a string that represents the user's password.
   */
  autenthicateByZuul(user: string, password: string) {
    this.authenticationService.login(user, password)
      .pipe(first())
      .subscribe(
        data => {
          if (data.code == 'R-0200') {
            this.getSecurity();
          } else {
            this.messageService.add({ severity: 'warn', summary: 'Authentication', detail: `Error: ${this.translations.lbl_login}, ${this.translations.lbl_then_try_it_again}` });
            // Autenticacion
          }
        },
        error => {
          this.messageService.add({ severity: 'warn', summary: 'Authentication', detail: `Error: ${this.translations.lbl_login}, ${this.translations.lbl_then_try_it_again}` });
          // Autenticacion
        });
  }

  /**
  * The function `getSecurity()` retrieves security data and performs additional actions if the data is
  * not already available.
  */
  async getSecurity() {
    const securityControl = this.globalUserPreferences || null;

    // ACTUALIZAMOS SEGURIDAD NUEVAMENTE Y TENTATIVAMENTE SE PUEDE ELIMINAR ESTOS BLOQUES
    if (!securityControl) {
      const tokenProfile = this.globalUserPreferences?.preferences?.id || null;
      const data = await this.apiService.findSecurityById({ id: tokenProfile });
      this.globalUserPreferences = data;
    }
    await this.getTranslatedLabels();
    await this.getInitialData();
    await this.getLanguages();
  }

  /**
   * The function `getTranslatedLabels` is an asynchronous function that retrieves translated labels from
   * a translation service and sets them in the `translations` variable, and then calls another function
   * `getLanguages`.
   */
  async getTranslatedLabels() {
    try {
      const language = this.globalUserPreferences?.preferences?.language;
      const region = this.globalUserPreferences?.preferences?.region;
      await this.translationService.getTranslatedLabels(language, region);
      this.hoursLabels = await this.apiService.listTranslatedServices({ "language": language });
      this.hourLabelsTranslated = await this.assignTranslatedServices();
      this.translations = this.translationService.getLabels();

    } catch (error) {
      console.error("An error occurred while getting translated labels:", error);
      this.messageService.add({ severity: 'warn', summary: 'Error', detail: `Error: ${this.translations.lbl_translation} ${this.translations.lbl_language}` });
      // Error al traer labels traducidos
    }
  }

  assignTranslatedServices() {
    let arr = {
      plw_adults: "",
      plw_yadults: "",
      plw_adol: "",
      plw_child: "",
      plw_mar_cour: "",
      plw_sfpg: "",
      mar_cour: ""
    };

    arr.plw_adults = this.hoursLabels[2].wsShrName;
    arr.plw_yadults = this.hoursLabels[3].wsShrName;
    arr.plw_adol = this.hoursLabels[4].wsShrName;
    arr.plw_child = this.hoursLabels[5].wsShrName;
    arr.plw_mar_cour = this.hoursLabels[6].wsShrName;
    arr.plw_sfpg = this.hoursLabels[12].wsShrName;
    arr.mar_cour = this.hoursLabels[6].wsName;

    return arr;
  }

  /**
   * The function `getLanguages` is an asynchronous function that fetches a list of languages and handles
   * any errors that occur during the process.
   */
  async getLanguages() {
    try {
      const language = this.globalUserPreferences?.preferences?.language || null;
      const storeId = this.globalUserPreferences?.preferences?.coordinationId || this.globalUserPreferences?.preferences?.storeId || null;

      const result = await this.tovpilCrudService.fetch(this.endPointLanguageByStoreId, {
        "language": language,
        "storeId": storeId
      });

      if (result.success) {
        this.languagesLst = result.data;
        if (language && storeId) {
          const selectedLanguage = this.languagesLst.find(o => o.value === language);

          this.globalUserDataForm.patchValue({
            selectedLanguage: selectedLanguage
          });
        }
      } else {

        this.messageService.add({ severity: 'warn', summary: this.translations.lbl_language, detail: `Error: ${this.translations.lbl_search} ${this.translations.lbl_language}` });
        // Error al extraer lenguajes
      }
    } catch (error) {

      this.messageService.add({ severity: 'warn', summary: this.translations.lbl_language, detail: `Error: ${this.translations.lbl_search} ${this.translations.lbl_language}` });
      // Error al extraer lenguajes
    }
  }

  /**
   * This function retrieves the initial data for the dropdowns and sets the states on the value
   * of the first index of arrays
   */
  async getInitialData() {
    try {
      await Promise.all([
        this.getValidYears(),
        this.getListCurrencyByStoreId()
      ]);
      this.globalUserDataForm.patchValue({
        selectedPeriod: this.periods[0],
        selectedYear: this.listYears[0],
        selectedCurrency: this.listCurrencies[0],
        selectedCoordination: this.globalUserPreferences?.preferences?.coordinationId || null
      });

      const coord = this.globalUserPreferences?.preferences?.storeSelected;
      this.coordName = coord.label;
      await this.getListStatistForFI();
    } catch (e) {
      console.error("Error al cargar los datos: ", e);
    }
  }

  /**
  * This function retrieves the valid years and sets the variable `listYears` with the result
  */
  async getValidYears() {
    let validYears = await this.apiService.getValidYears();
    this.listYears = validYears;
  }

  /**
   * This function retrieves the currencies and sets the variable `listCurrencies` with the result
   */
  async getListCurrencyByStoreId() {
    let curr = await this.apiService.getListCurrencyByStoreId({
      "storeId": this.globalUserPreferences?.preferences?.coordinationId || null,
      "language": this.globalUserPreferences?.preferences?.language || null
    });
    this.listCurrencies = curr;
  }

  /**
   * This function retrieves the data to display in the table
   */
  async getListStatistForFI() {
    try {

      const isPeriodValid = this.globalUserDataForm.get('selectedPeriod').valid;
      const isYearValid = this.globalUserDataForm.get('selectedYear').valid;
      const isCurrencyuValid = this.globalUserDataForm.get('selectedCurrency').valid;
      this.blocked = true;

      if (isPeriodValid && isYearValid && isCurrencyuValid) {
        let dataServ = await this.apiService.listStatistForFI({
          "period": this.globalUserDataForm.get('selectedPeriod').value.key,
          "year": this.globalUserDataForm.get('selectedYear').value.year,
          "storeId": this.globalUserDataForm.get('selectedCoordination').value ?? this.security.storeId,
          "currCode": this.globalUserDataForm.get('selectedCurrency').value.valueId,
        });
        this.data = dataServ;

        this.blocked = false;
      }
      else {
        this.messageService.add({ severity: 'warn', summary: 'INVALID FORM', detail: `Error: NECESITAS TODOS LOS DATOS` });
      }
      this.blocked = false;
    } catch (e) {
      this.messageService.add({ severity: 'warn', summary: 'INVALID FORM', detail: `Error: NECESITAS TODOS LOS DATOS` });
      this.blocked = false;
    }
  }

  /**
   * The function `sumColumns` get the adition of all fields in the row 
   */
  sumColumns(fila: any): number {
    return fila.totOfferingAdu + fila.totOfferingYng + fila.totOfferingTee + fila.totOfferingChi + fila.totOfferingMar + fila.totOfferingSTI;
  }

  /**
   * The function `sumRows` get the adition of all columns
   */
  sumRows(columna: string): number {
    if (this.data != null) {
      return this.data.reduce((suma, fila) => suma + fila[columna], 0);
    }
  }

  /**
   * The function `totalRows` get the adition of all rows for the last
   * cell of the table
   */
  totalRows() {
    if (this.data != null) {
      return this.sumRows('totOfferingAdu') +
        this.sumRows('totOfferingYng') +
        this.sumRows('totOfferingTee') +
        this.sumRows('totOfferingChi') +
        this.sumRows('totOfferingMar') +
        this.sumRows('totOfferingSTI');
    }
  }

  async goBack() {
    try {
      let navigationExtras: NavigationExtras = {
        state: JSON.parse(JSON.stringify({
          selStore: Constants.selStore,
          itemsNavigation: Constants.itemsNavigation,
          module: 'secretary',
          view: 'FISummary'
        }))
      };

      Constants.selStore = null;
      Constants.itemsNavigation = null;
      try {
        this.router.navigate(['/commons/lcSelector'], navigationExtras);

      } catch (navigationError) {
        console.error("An error occurred while navigating to coordination catalog details:", navigationError);
        this.handleError('Redirigir', 'Algo salio mal al redirigir ');
      }
    } catch (parseError) {
      console.error("An error occurred while parsing state to JSON:", parseError);
      this.handleError('Redirigir', 'Algo salio mal al redirigir');
    }
  }

  /**
 * The function initializes form groups for configuration and data, and updates the configuration form
 * with parameters from the cookie.
 */
  async initConfigs() {
    // CONSULTAMOS COOKIES
    this.globalUserPreferences = await this.userPreferencesService.getSantovTovpilCookie() || null;
    console.log("FiSummary() Preferencias de usuario: " + JSON.stringify(this.globalUserPreferences, null, 2));
    // console.debug("FiSummary() Preferencias de usuario: " + JSON.stringify(this.globalUserPreferences, null, 2));
  }

  /**
   * The function `selectIncident` navigates to the incident review detail page with specified query
   * parameters.
   * @param {string} [id] - The `id` parameter in the `selectIncident` method is a string that represents
   * the incident ID. It is used to navigate to the incident review details page with the specified
   * incident ID as a query parameter.
   */
  selectCoordination(id?: string) {
    this.router.navigate(["secretary/FISummary"], {
      queryParams: {
        coordinationId: id
      },
    });
  }
  private handleError(summary: string, detail: string) {
    console.error("An error occurred while parsing.");
    this.messageService.add({
      severity: 'error',
      summary: summary,
      detail: detail,
      life: 5000
    });
  }
  async onClickDownloadFile() {
    try {
      this.blocked = true;
      await this.apiService.dwnFIOffering(
        {
          "storeId": Constants.selStore.storeId,
          "year": this.globalUserDataForm.get("selectedYear").value.year,
          "period": this.globalUserDataForm.get("selectedPeriod").value.key,
          "currCode": this.globalUserDataForm.get("selectedCurrency").value.valueId,
          "language": this.security?.language
        }
      ).then(
        data => {
          this.responseFile = data;

          const source = `data:application/octet-stream;base64,${this.responseFile.base64File}`;

          const link = document.createElement("a");
          link.href = source;

          link.download = this.responseFile.nameFile
          link.click();


        })
    } catch (error) {
      console.error("An error occurred while downloading file:", error);
      this.messageService.add({ severity: 'warn', summary: 'Error', detail: `Error:  ${error}` });
    } finally {
      this.blocked = false;
    }
  }

  /**
    * The function `createDataForm` in TypeScript initializes a form group with various form controls and
    * default values, as well as setting some controls as disabled.
    */
  createDataForm() {
    this.globalUserDataForm = this.fb.group({
      selectedLanguage: new FormControl({ value: null }),
      selectedPeriod: new FormControl({ value: null }),
      selectedCurrency: new FormControl({ value: null }),
      selectedCoordination: new FormControl({ value: null, disabled: true }),
      selectedYear: new FormControl({ value: null })
    });
  }


  /* The above TypeScript code is updating properties of a user object, specifically setting the
      languageId, currencyId, periodYear, and periodMonth based on the provided values or null if not
      provided. It also updates the periodSelected property with the provided period value. Finally,
      it updates a cookie named "userPreferences" with the updated globalUserPreferences object using
      a cookie service. */
  async refreshPreferences(coordinationId) {
    // SOLO SE ACTUALIZA LA COOKIE
    const language = (this.globalUserDataForm.get('selectedLanguage').value)?.valueId || null;
    const period = (this.globalUserDataForm.get('selectedPeriod').value) || null;
    const currency = (this.globalUserDataForm.get('selectedCurrency').value)?.valueId || null;
    const year = (this.globalUserDataForm.get('selectedYear').value)?.valueId || null;
    let periodSel = this.globalUserPreferences.preferences.periodSelected;

    // ACTUALIZAMOS PROPIEDADES DE USUARIO
    // ACTUALIZAMOS PROPIEDADES DE USUARIO
    this.userPreferencesService.updateTovpilCookie(
      null, // _levelId
      null, //_userId
      null, //_storeId
      null, //_id
      language || null, //_language
      currency || null, //_selCurrency
      coordinationId || null, //_coordinationId
      null, //_coordinationStack
      year || null, //_selYear
      period?.key || null, //_selPeriod
      null, //_device
      null, //_storeSelected
      periodSel, //_periodSelected
      null //_stores
    );
  }

  redirectoToJrnHGuideReferInc(coordinationId) {
    try {
      // HERE UPDATE COOKIES
      this.refreshPreferences(coordinationId);
      try {
        const queryParams = {

        }

        const url = this.urlBuilderService.buildUrl('/treasurer/jrnHGuideReferInc', queryParams);
        this.router.navigateByUrl(url);
      } catch (navigationError) {
        console.error("An error occurred while navigating to coordination catalog details:", navigationError);
        //this.handleError('Redirigir a CX4', 'Algo salio mal al redirigir a CX4');
      }
    } catch (parseError) {
      console.error("An error occurred while parsing state to JSON:", parseError);
      //this.handleError('Redirigir a CX4', 'Algo salio mal al redirigir a CX4');
    }
  }
}
